/**
 * React Query hooks for user profile management
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { userProfileService } from '../services/userProfile';
import { useAuthStore } from '../store/authStore';
import type {
  UpdateProfileData,
  ChangePasswordData,
  DisableUserAccountData,
  UpdateNotificationSettingsData,
} from '../types';
import toast from 'react-hot-toast';

/**
 * Update user profile
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateProfileData) => userProfileService.updateProfile(data),
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to update profile';
      toast.error(message);
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Profile updated successfully');
      // Update the user in auth store
      useAuthStore.getState().fetchUser();
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  });
}

/**
 * Change user password
 */
export function useChangePassword() {
  return useMutation({
    mutationFn: (data: ChangePasswordData) => userProfileService.changePassword(data),
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to change password';
      toast.error(message);
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Password changed successfully');
    },
  });
}

/**
 * Fetch the user's Telegram notification settings
 */
export function useNotificationSettings() {
  return useQuery({
    queryKey: ['notification-settings'],
    queryFn: () => userProfileService.getNotificationSettings(),
  });
}

/**
 * Update the user's Telegram notification settings
 */
export function useUpdateNotificationSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateNotificationSettingsData) =>
      userProfileService.updateNotificationSettings(data),
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to update notification settings';
      toast.error(message);
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Notification settings updated');
      // Refresh both the settings query and the auth user (toggles live on user)
      queryClient.setQueryData(['notification-settings'], data.settings);
      queryClient.invalidateQueries({ queryKey: ['notification-settings'] });
      useAuthStore.getState().fetchUser();
    },
  });
}

/**
 * Disable user account
 */
export function useDisableUserAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: DisableUserAccountData) => userProfileService.disableUserAccount(data),
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to disable account';
      toast.error(message);
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Account disabled successfully');
      // Clear all queries since account is disabled
      queryClient.clear();
      // Redirect will be handled by the component
    },
  });
}
